import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_BASE } from '../services/api';

interface Proveedor {
  id: number;
  razon_social: string;
  nit: string;
  email: string;
  telefono: string;
  username: string;
  estado: string;
}

interface AuthContextType {
  proveedor: Proveedor | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string, recaptchaToken?: string) => Promise<void>;
  loginWithAzure: (token: string, userData: any) => Promise<void>;
  register: (data: any) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [proveedor, setProveedor] = useState<Proveedor | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!token && !!proveedor;

  useEffect(() => {
    const savedToken = localStorage.getItem('auth_token');
    const savedProveedor = localStorage.getItem('auth_proveedor');
    
    if (savedToken && savedProveedor) {
      try {
        setToken(savedToken);
        setProveedor(JSON.parse(savedProveedor));
      } catch (error) {
        console.error('Error parsing saved auth data:', error);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_proveedor');
      }
    }
    
    setIsLoading(false);
  }, []);

  const login = async (username: string, password: string, recaptchaToken?: string): Promise<void> => {
    try {
      console.log('🔐 Intentando login:', username);
      
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, recaptcha_token: recaptchaToken || '' }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error en el login');
      }

      setToken(data.access_token);
      setProveedor(data.proveedor);
      
      localStorage.setItem('auth_token', data.access_token);
      localStorage.setItem('auth_proveedor', JSON.stringify(data.proveedor));
      
      console.log('✅ Login exitoso:', data.proveedor.username);
    } catch (error) {
      console.error('❌ Error en login:', error);
      throw error;
    }
  };

  const loginWithAzure = async (azureToken: string, userData: any): Promise<void> => {
    try {
      console.log('🔐 Login con Azure AD:', userData);
      
      const response = await fetch(`${API_BASE}/api/auth/azure-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: azureToken, user: userData }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error en login Azure');
      }

      setToken(data.access_token);
      setProveedor(data.proveedor);
      
      localStorage.setItem('auth_token', data.access_token);
      localStorage.setItem('auth_proveedor', JSON.stringify(data.proveedor));
      
      console.log('✅ Login Azure exitoso:', data.proveedor.username);
    } catch (error) {
      console.error('❌ Error en login Azure:', error);
      throw error;
    }
  };

  const register = async (data: any): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/registro-simple`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Error en el registro');
      }

      console.log('✅ Registro exitoso:', result.username);
    } catch (error) {
      console.error('❌ Error en registro:', error);
      throw error;
    }
  };

  const logout = () => {
    setToken(null);
    setProveedor(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_proveedor');
    console.log('👋 Logout exitoso');
  };

  return (
    <AuthContext.Provider value={{
      proveedor,
      token,
      isAuthenticated,
      isLoading,
      login,
      loginWithAzure,
      register,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;

